import { Intercom } from "@capacitor-community/intercom";
import { show, update } from "@intercom/messenger-js-sdk";
import { useQuery } from "@tanstack/react-query";
import { FC, SVGProps } from "react";

import { useTranslation } from "@/core/i18n";
import { useAdonisChat } from "@/modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { useAgelessCapabilities } from "@/modules/ageless/useAgelessCapabilities/useAgelessCapabilities";
import { getPlatform, getPlatformLocation } from "api/methods";
import AgelessIcon from "atoms/icon/icons/ageless-a.svg";
import Help from "atoms/icon/icons/help.svg";
import Account from "atoms/icon/icons/ic_account_small_ol.svg";
import Admin from "atoms/icon/icons/ic_admin_small_ol.svg";
import Logout from "atoms/icon/icons/ic_logout_small_ol.svg";
import MapPin from "atoms/icon/icons/ic_map_pin_ol.svg";
import OrderHistory from "atoms/icon/icons/ic_orderhistory_small_ol.svg";
import QRCode from "atoms/icon/icons/ic_qr_code_small_ol.svg";
import Rewards from "atoms/icon/icons/ic_rewards_ol.svg";
import Notification from "atoms/icon/icons/notification.svg";
import Phone from "atoms/icon/icons/phone.svg";
import { pushNotificationService } from "modules/pushNotifications";
import { buildDebugContext } from "utils/debugContext";
import { getAdminUrl } from "utils/functions";
import { isNativeMobileApp } from "utils/platform.helpers";
import { useLogout } from "utils/useLogout";

/** Which block of the account list an entry belongs to. */
export type AccountLinkGroup = "profile" | "main" | "admin" | "logout";

export type AccountLink = {
  key: string;
  label: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  group: AccountLinkGroup;
  /** In-app destination. */
  to?: string;
  /** External destination, opened as a real anchor (Admin portal). */
  href?: string;
  onClick?: () => void;
};

/**
 * The account menu, filtered by platform settings and runtime capabilities.
 *
 * Shared by the phone list (`AccountPage`) and the desktop rail
 * (`AccountNavRail`) so the two cannot drift apart.
 */
export const useAccountLinks = (): AccountLink[] => {
  const { t } = useTranslation();
  const { isPersonalisationEnabled } = useAdonisChat();
  const { capabilities: agelessCapabilities } = useAgelessCapabilities();

  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { data: platformLocation } = useQuery({ ...getPlatformLocation.keyed() });

  const logout = useLogout({ subdomain: platform?.subdomain });

  const handleContactUsClick = async () => {
    const debugContext = await buildDebugContext();
    const debugContextStr = JSON.stringify(debugContext);

    if (isNativeMobileApp()) {
      try {
        await Intercom.updateUser({
          customAttributes: {
            Patient: true,
            debugContext: debugContextStr,
          },
        });
      } catch (e) {
        console.error("[Intercom] updateUser error", e);
      }
      Intercom.displayMessenger();
    } else {
      try {
        update({ Patient: true, debugContext: debugContextStr });
      } catch (e) {
        console.error("[Intercom] update error", e);
      }
      show();
    }
  };

  const handlePhoneCallClick = () => {
    if (platformLocation?.phoneNumber) {
      window.open(`tel:${platformLocation.phoneNumber}`);
    }
  };

  const links: (AccountLink | false | undefined)[] = [
    {
      key: "details",
      group: "profile",
      to: "/account/details",
      icon: Account,
      label: t("account.accountDetails"),
    },
    {
      key: "orders",
      group: "main",
      to: "/account/orders",
      icon: OrderHistory,
      label: t("account.orderHistory"),
    },
    platform?.platformSettings?.rewardsEnabled && {
      key: "reward-history",
      group: "main",
      to: "/account/reward-history",
      icon: Rewards,
      label: t("account.rewardHistory"),
    },
    {
      key: "visits",
      group: "main",
      to: "/account/visits",
      icon: QRCode,
      label: t("account.visitHistory"),
    },
    agelessCapabilities?.isEnabled && {
      key: "assessments",
      group: "main",
      to: "/account/assessments",
      icon: AgelessIcon,
      label: t("account.treatmentTryOnHistory"),
    },
    {
      key: "support",
      group: "main",
      icon: Help,
      label: t("account.appSupport"),
      onClick: handleContactUsClick,
    },
    {
      key: "contact",
      group: "main",
      icon: Phone,
      label: t("account.contactUs"),
      onClick: handlePhoneCallClick,
    },
    isNativeMobileApp() && {
      key: "notifications",
      group: "main",
      icon: Notification,
      label: t("account.notificationSettings"),
      onClick: () => pushNotificationService.openAppSettings(),
    },
    isNativeMobileApp() && {
      key: "select-location",
      group: "main",
      to: "/platform-selector",
      icon: MapPin,
      label: t("account.selectLocation"),
    },
    isPersonalisationEnabled && {
      key: "personalise",
      group: "main",
      to: "/personalise",
      icon: Account,
      label: t("account.personalization"),
    },
    !isNativeMobileApp() && {
      key: "admin",
      group: "admin",
      href: getAdminUrl(),
      icon: Admin,
      label: t("account.admin"),
    },
    {
      key: "logout",
      group: "logout",
      icon: Logout,
      label: t("account.logout"),
      onClick: logout,
    },
  ];

  return links.filter((link): link is AccountLink => !!link);
};
